import { Tooltip } from '@affine/component/ui/tooltip';
import { useCatchEventCallback } from '@affine/core/components/hooks/use-catch-event-hook';
import { SubscriptionPlan } from '@affine/graphql';
import { useI18n } from '@affine/i18n';
import { useLiveData, useServices } from '@toeverything/infra';
import { useSetAtom } from 'jotai';
import { useEffect, useMemo } from 'react';

import {
  ServerConfigService,
  SubscriptionService,
} from '../../../modules/cloud';
import { openSettingModalAtom } from '../../atoms';
import * as styles from './style.css';

export const UserPlanButton = () => {
  const t = useI18n();

  const { serverConfigService, subscriptionService } = useServices({
    ServerConfigService,
    SubscriptionService,
  });

  const hasPayment = useLiveData(
    serverConfigService.serverConfig.features$.map(r => r?.payment)
  );

  const plan =
    useLiveData(subscriptionService.subscription.subscription$)?.[0]?.plan ??
    SubscriptionPlan.Free;
  const isBeliever = useLiveData(subscriptionService.subscription.isBeliever$);
  const isLoading = plan === null;

  const setSettingModalAtom = useSetAtom(openSettingModalAtom);
  const handleClick = useCatchEventCallback(() => {
    setSettingModalAtom({
      open: true,
      activeTab: 'plans',
    });
  }, [setSettingModalAtom]);

  const planLabel = useMemo(() => {
    if (isBeliever) {
      return t['com.affine.payment.lifetime.title']();
    }
    if (plan === SubscriptionPlan.Basic) {
      return t['com.affine.payment.cloud.basic.name']();
    }
    if (plan === SubscriptionPlan.Pro) {
      return t['com.affine.payment.cloud.pro.name']();
    }
    return t['com.affine.payment.cloud.free.name']();
  }, [isBeliever, plan, t]);

  useEffect(() => {
    // revalidate subscription to get the latest status
    subscriptionService.subscription.revalidate();
  }, [subscriptionService]);

  if (!hasPayment) {
    // no payment feature
    return;
  }

  if (isLoading) {
    // loading, do nothing
    return;
  }

  return (
    <Tooltip content={t['com.affine.payment.tag-tooltips']()} side="top">
      <div
        data-is-believer={isBeliever ? 'true' : undefined}
        className={styles.userPlanButton}
        onClick={handleClick}
        data-event-props="$.settingsPanel.profileAndBadge.viewPlans"
      >
        {planLabel}
      </div>
    </Tooltip>
  );
};
